import { useEffect, useRef } from 'react';
import { UseFormReturn } from 'react-hook-form';
import { ConfigurationGroupType, TimeSlotType } from 'shared-types/SpaPackage';

import { isTimeslotRequired, SpaPackageFormValues } from '../../SpaPackageForm.schema';
import {
  buildTreatmentSchedule,
  freeSlots,
  isBetterSchedule,
  isOverBudget,
  SchedulableTreatment,
  ScheduleSlot,
  SearchBudget,
  slotsOverlap,
  TreatmentSchedule,
} from '../buildTreatmentSchedule';
import { collectBusyIntervals, shouldRenderConfigGroup } from '../SpaDayBooking.helpers';
import { getTreatmentOptionRefs, isTreatmentRef } from '../timeslotClash';

type Guest = SpaPackageFormValues['guests'][number];
type ConfigurationOption = ConfigurationGroupType['options'][number];
type BookableSlot = ScheduleSlot & Partial<Pick<TimeSlotType, 'staff' | 'rooms'>>;
type ResourceKind = 'staff' | 'rooms';

type UsePrefillTreatmentTimesParams = {
  enabled: boolean;
  form: UseFormReturn<SpaPackageFormValues>;
  configGroupsNoExtras: ConfigurationGroupType[];
  watchedGuests: SpaPackageFormValues['guests'] | undefined;
  arrivalStartTime: string | undefined;
  selectedDate: string;
  requireTimeslots: boolean;
};

type PendingTreatment = {
  key: string;
  groupKey: string;
  selectionIndex: number;
  optionId: number;
  slots: TimeSlotType[];
};

type PlannedTime = {
  treatment: PendingTreatment;
  slot: TimeSlotType;
};

type Reservation = {
  slot: ScheduleSlot;
  ids: string[];
};

type Taken = {
  sessionsByOption: Map<number, ScheduleSlot[]>;
  reservations: Record<ResourceKind, Reservation[]>;
};

type PlanContext = {
  groups: ConfigurationGroupType[];
  treatmentOptions: Set<string>;
  replaceExisting: boolean;
  requireTimeslots: boolean;
};

const RESOURCE_KINDS: ResourceKind[] = ['staff', 'rooms'];

const optionSlots = (option: ConfigurationOption): TimeSlotType[] =>
  Array.isArray(option.timeslots) ? option.timeslots : [];

const knownIds = (slot: BookableSlot, kind: ResourceKind): string[] | null => {
  const ids = slot[kind];
  return Array.isArray(ids) && ids.length > 0 ? ids : null;
};

function buildAvailabilitySignature(
  groups: ConfigurationGroupType[],
  selectedDate: string,
  arrivalStartTime: string | undefined
): string {
  const groupPart = groups
    .map((group) =>
      group.options
        .map(
          (option) =>
            `${option.id}:${optionSlots(option)
              .map((slot) => slot.startTime)
              .join(',')}`
        )
        .join(';')
    )
    .join('|');
  return `${selectedDate}#${arrivalStartTime ?? ''}#${groupPart}`;
}

function findOfferedSlot(
  groups: ConfigurationGroupType[],
  optionId: number,
  startTime: string
): TimeSlotType | undefined {
  for (const group of groups) {
    const option = group.options.find((candidate) => candidate.id === optionId);
    const offered = option && optionSlots(option).find((slot) => slot.startTime === startTime);
    if (offered) return offered;
  }
  return undefined;
}

function capacityById(ids: string[]): Map<string, number> {
  const capacity = new Map<string, number>();
  ids.forEach((id) => capacity.set(id, (capacity.get(id) ?? 0) + 1));
  return capacity;
}

function overlappingGroup(candidate: Reservation, reservations: Reservation[]): Reservation[] {
  const group = [candidate];
  const unvisited = [...reservations];
  for (let index = 0; index < group.length; index++) {
    for (let other = unvisited.length - 1; other >= 0; other--) {
      if (slotsOverlap(group[index].slot, unvisited[other].slot)) {
        group.push(unvisited[other]);
        unvisited.splice(other, 1);
      }
    }
  }
  return group;
}

function canAssignResources(reservations: Reservation[]): boolean {
  const ordered = reservations
    .map((reservation) => ({ ...reservation, capacity: capacityById(reservation.ids) }))
    .sort((a, b) => a.capacity.size - b.capacity.size);
  const assigned: { slot: ScheduleSlot; id: string }[] = [];

  const assignFrom = (index: number): boolean => {
    if (index === ordered.length) return true;
    const { slot, capacity } = ordered[index];
    for (const [id, limit] of Array.from(capacity.entries())) {
      const clashes = assigned.filter(
        (entry) => entry.id === id && slotsOverlap(entry.slot, slot)
      ).length;
      if (clashes >= limit) continue;
      assigned.push({ slot, id });
      if (assignFrom(index + 1)) return true;
      assigned.pop();
    }
    return false;
  };

  return assignFrom(0);
}

function hasResourcesFor(slot: BookableSlot, taken: Taken): boolean {
  return RESOURCE_KINDS.every((kind) => {
    const ids = knownIds(slot, kind);
    if (!ids) return true;
    const candidate = { slot: { startTime: slot.startTime, endTime: slot.endTime }, ids };
    return canAssignResources(overlappingGroup(candidate, taken.reservations[kind]));
  });
}

function recordTaken(taken: Taken, optionId: number, slot: BookableSlot) {
  const interval = { startTime: slot.startTime, endTime: slot.endTime };
  taken.sessionsByOption.set(optionId, [...(taken.sessionsByOption.get(optionId) || []), interval]);
  RESOURCE_KINDS.forEach((kind) => {
    const ids = knownIds(slot, kind);
    if (ids) taken.reservations[kind].push({ slot: interval, ids });
  });
}

function collectTaken(guests: Guest[], groups: ConfigurationGroupType[]): Taken {
  const taken: Taken = {
    sessionsByOption: new Map(),
    reservations: { staff: [], rooms: [] },
  };
  guests.forEach((guest) => {
    Object.values(guest.configurationGroups || {}).forEach((selections) => {
      (selections || []).forEach((selection) => {
        if (!selection?.isSelected || !selection.timeslot?.startTime) return;
        const offered = findOfferedSlot(groups, selection.optionId, selection.timeslot.startTime);
        recordTaken(taken, selection.optionId, offered ?? selection.timeslot);
      });
    });
  });
  return taken;
}

function isSlotAvailable(slot: TimeSlotType, optionId: number, taken: Taken): boolean {
  const concurrent = (taken.sessionsByOption.get(optionId) || []).filter((session) =>
    slotsOverlap(session, slot)
  ).length;
  if (slot.quantity != null && concurrent >= slot.quantity) return false;
  return hasResourcesFor(slot, taken);
}

function collectPendingTreatments(guest: Guest, context: PlanContext): PendingTreatment[] {
  const { groups, replaceExisting, requireTimeslots } = context;
  const pending: PendingTreatment[] = [];
  groups.forEach((group) => {
    const groupKey = `group_${group.id}`;
    const selections = guest.configurationGroups?.[groupKey] || [];
    selections.forEach((selection, selectionIndex) => {
      if (!selection) return;
      if (!replaceExisting && selection.timeslot) return;
      if (!isTimeslotRequired(selection, group, requireTimeslots)) return;
      const option = group.options.find((candidate) => candidate.id === selection.optionId);
      const slots = option ? optionSlots(option) : [];
      if (slots.length === 0) return;
      pending.push({
        key: `${groupKey}:${selectionIndex}`,
        groupKey,
        selectionIndex,
        optionId: selection.optionId,
        slots,
      });
    });
  });
  return pending;
}

function subsetsOfSize<T>(items: T[], size: number): T[][] {
  const subsets: T[][] = [];
  const walk = (index: number, acc: T[]) => {
    if (acc.length === size) {
      subsets.push(acc);
      return;
    }
    if (items.length - index < size - acc.length) return;
    walk(index + 1, [...acc, items[index]]);
    walk(index + 1, acc);
  };
  walk(0, []);
  return subsets;
}

function scheduleAsManyAsPossible(
  open: SchedulableTreatment[],
  fixed: ScheduleSlot[]
): TreatmentSchedule | null {
  const placeable = open.filter((treatment) => freeSlots(treatment.slots, fixed).length > 0);
  const budget: SearchBudget = { steps: 0 };
  for (let size = placeable.length; size > 0; size--) {
    let best: TreatmentSchedule | null = null;
    for (const subset of subsetsOfSize(placeable, size)) {
      const candidate = buildTreatmentSchedule(subset, fixed, budget);
      if (candidate && (!best || isBetterSchedule(candidate, best))) best = candidate;
      if (best?.totalGap === 0 || isOverBudget(budget)) break;
    }
    if (best || isOverBudget(budget)) return best;
  }
  return null;
}

function planGuest(guest: Guest, context: PlanContext, taken: Taken): PlannedTime[] {
  const pending = collectPendingTreatments(guest, context);
  if (pending.length === 0) return [];

  const fixed = context.replaceExisting
    ? []
    : collectBusyIntervals(guest.configurationGroups, undefined, undefined, (selection) =>
        isTreatmentRef(context.treatmentOptions, selection)
      );

  const open: SchedulableTreatment[] = pending.map((treatment) => ({
    key: treatment.key,
    slots: treatment.slots.filter((slot) => isSlotAvailable(slot, treatment.optionId, taken)),
  }));

  const schedule = scheduleAsManyAsPossible(open, fixed);
  if (!schedule) return [];

  const pendingByKey = new Map(pending.map((treatment) => [treatment.key, treatment]));
  return schedule.treatments.flatMap(({ key, slot }) => {
    const treatment = pendingByKey.get(key);
    const offered = treatment?.slots.find(
      (candidate) => candidate.startTime === slot.startTime && candidate.endTime === slot.endTime
    );
    return treatment && offered ? [{ treatment, slot: offered }] : [];
  });
}

function applyPlan(guest: Guest, planned: PlannedTime[]): Guest {
  const configGroups = { ...(guest.configurationGroups || {}) };

  planned.forEach(({ treatment, slot }) => {
    const selections = [...(configGroups[treatment.groupKey] || [])];
    const current = selections[treatment.selectionIndex];
    if (!current) return;
    selections[treatment.selectionIndex] = {
      ...current,
      timeslot: { startTime: slot.startTime, endTime: slot.endTime },
    };
    configGroups[treatment.groupKey] = selections;
  });

  return { ...guest, configurationGroups: configGroups };
}

export function usePrefillTreatmentTimes({
  enabled,
  form,
  configGroupsNoExtras,
  watchedGuests,
  arrivalStartTime,
  selectedDate,
  requireTimeslots,
}: UsePrefillTreatmentTimesParams) {
  const lastSignatureRef = useRef<string | null>(null);

  useEffect(() => {
    if (!enabled || !requireTimeslots) return;

    const groups = configGroupsNoExtras.filter(shouldRenderConfigGroup);
    if (groups.length === 0) return;

    const currentGuests = form.getValues('guests') || [];
    if (currentGuests.length === 0) return;

    const signature = buildAvailabilitySignature(groups, selectedDate, arrivalStartTime);
    const replaceExisting =
      lastSignatureRef.current !== null && lastSignatureRef.current !== signature;
    lastSignatureRef.current = signature;

    const context: PlanContext = {
      groups,
      treatmentOptions: getTreatmentOptionRefs(groups),
      replaceExisting,
      requireTimeslots,
    };
    const taken = collectTaken(replaceExisting ? [] : currentGuests, groups);

    let changed = false;
    const nextGuests = currentGuests.map((guest) => {
      const planned = planGuest(guest, context, taken);
      if (planned.length === 0) return guest;
      planned.forEach(({ treatment, slot }) => recordTaken(taken, treatment.optionId, slot));
      changed = true;
      return applyPlan(guest, planned);
    });

    if (changed) {
      form.setValue('guests', nextGuests as SpaPackageFormValues['guests']);
    }
  }, [
    enabled,
    form,
    configGroupsNoExtras,
    watchedGuests,
    arrivalStartTime,
    selectedDate,
    requireTimeslots,
  ]);
}
